import { useEffect, useRef, useState } from "react"
import useWindowWidth from "../../hooks/useWindowWidth"
import PropTypes from "prop-types"
import ReactHtmlParser from "react-html-parser"
// import { isMobile, isAndroid, isIOS } from "react-device-detect"
import Svg from "../_svgs/svg"

function ColumnLinks({ columnLinks, closeFooterMenuLabel, openFooterMenuLabel }) {
  const [isOpen, setIsOpen] = useState(false)
  const windowWidth = useWindowWidth()

  const handleToggle = () => {
    // screen-md
    if (windowWidth < 950) {
      setIsOpen((open) => !open)
    } else {
      setIsOpen(true)
    }
  }

  useEffect(() => {
    if (windowWidth >= 950) {
      setIsOpen(true)
    }
  }, [windowWidth])

  if (!columnLinks) return null
  const { columnLinks: links, headerLink, headerLinkText, headerLinkTarget } = columnLinks

  const desktopTopHeader = (
    <div className="footer-navigation__links__header">
      <p className="topmenuitem">
        <span className="text">{headerLinkText}</span>
      </p>
    </div>
  )

  const headerLinkIds = headerLinkText.replaceAll(" ", "-")

  const mobileTopHeader = (
    <button
      className="footer-navigation__links__header"
      onClick={handleToggle}
      aria-expanded={isOpen}
      aria-controls={headerLinkIds}
      aria-label={
        isOpen
          ? (openFooterMenuLabel && openFooterMenuLabel.formatUnicorn({ header: headerLinkText })) || "Toggle Menu"
          : (closeFooterMenuLabel && closeFooterMenuLabel.formatUnicorn({ header: headerLinkText })) || "Toggle Menu"
      }
    >
      <p className="topmenuitem">
        <span className="text">{headerLinkText}</span>
      </p>
      <Svg iconName="arrowdownrounded" isDecorative={true} iconClass={isOpen ? "icon active" : "icon"} />
    </button>
  )

  return (
    <div className="footer-navigation__links">
      {windowWidth < 950 ? mobileTopHeader : desktopTopHeader}
      {windowWidth < 950 ? (
        <div className={`contentWrapper ${isOpen ? " is-active" : ""}`}>
          <LinkList id={headerLinkIds} isOpen={isOpen} columnLinks={links} />
        </div>
      ) : (
        <LinkList isOpen={isOpen} columnLinks={links} />
      )}
    </div>
  )
}

ColumnLinks.propTypes = {
  columnLinks: PropTypes.shape({
    columnLinks: PropTypes.array,
    headerLink: PropTypes.string,
    headerLinkText: PropTypes.string,
    headerLinkTarget: PropTypes.string,
  }),
}

function LinkList({ isOpen, columnLinks, id = undefined }) {
  if (!columnLinks || columnLinks.length === 0) return null
  return (
    <ul id={id || ""} className={`links-list ${isOpen ? "open" : "closed"}`}>
      {columnLinks.map((link, index) => (
        <li key={index} className={`links-list__item ${isOpen ? "" : ""}`}>
          <a
            tabIndex={isOpen ? "" : "-1"}
            aria-hidden={isOpen ? "false" : "true"}
            className="button button--link has-icon"
            target={link.target}
            href={link.href}
          >
            <span className="text">{link.text}</span>
          </a>
        </li>
      ))}
    </ul>
  )
}

LinkList.propTypes = {
  columnLinks: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      href: PropTypes.string,
      text: PropTypes.string,
    }),
  ),
}

function FooterBottom({ legalLinks, logo, logoMobile, copyrightText, copyrightTextMobile, logoLabel }) {
  const containerRef = useRef(null)
  const windowWidth = useWindowWidth()

  // needed to show dividers like the customer wants them
  useEffect(() => {
    const root = containerRef.current
    if (!root) return

    const calc = () => {
      const items = Array.from(root.querySelectorAll(".button--link"))
      if (!items.length) return

      items.forEach((i) => i.classList.remove("row-start"))

      let prevTop = Math.round(items[0].getBoundingClientRect().top)
      for (let i = 1; i < items.length; i++) {
        const top = Math.round(items[i].getBoundingClientRect().top)
        if (top !== prevTop) {
          items[i].classList.add("row-start")
          prevTop = top
        }
      }
    }

    // run once after first paint
    const run = () => setTimeout(calc, 0)

    // debounce resize so it doesn’t flip on every pixel
    let t
    const onResize = () => {
      clearTimeout(t)
      t = setTimeout(calc, 150)
    }

    run()
    window.addEventListener("resize", onResize, { passive: true })

    // recalc after fonts finish loading (prevents “sometimes wrong” states)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(calc).catch(() => {})
    }

    return () => {
      clearTimeout(t)
      window.removeEventListener("resize", onResize)
    }
  }, [])

  return (
    <div className="site-footer__bottom">
      <div className="w--wider">
        <div className="bottom-container">
          <div className="leftside">
            {ReactHtmlParser(windowWidth < 950 ? copyrightTextMobile : copyrightText)}

            {legalLinks?.length > 0 && (
              <div ref={containerRef} className="legal-links">
                {legalLinks.map((link, i) => (
                  <a key={i} className="button button--link" target={link.target} href={link.href}>
                    <span className="text">{link.text}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="footerlogo">
            <a className="button button--logo" href="/">
              {windowWidth < 950 ? (
                <Svg label={logoLabel || "Logo"} iconName={logoMobile} iconClass="footer-logo-mobile" />
              ) : (
                <Svg label={logoLabel || "Logo"} iconName={logo} iconClass="footer-logo" />
              )}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

function FooterMobile(props) {
  const {
    column1Links,
    column2Links,
    column3Links,
    column4Links,
    // appImage,
    // downloadAppText,
    // appStoreLink,
    // appStoreImage,
    // playStoreLink,
    // playStoreImage,
    socialLinks,
    legalLinks,
    logoMobile,
    copyrightText,
    copyrightTextMobile,
    redirectUrl,
    redirectUrlLabel,
    redirectUrlTarget,
    contactInfoRichText,
    logoLabel,
    openFooterMenuLabel,
    closeFooterMenuLabel,
  } = props

  return (
    <div className="footer-with-mobile-accordion">
      <div className="footer-container-new">
        <div className="w--wider">
          <div className="grid no-gutter">
            <div className="grid-item col--12 col-sm--7 col-md--7">
              <ColumnLinks columnLinks={column1Links} openFooterMenuLabel={openFooterMenuLabel} closeFooterMenuLabel={closeFooterMenuLabel} />
              <ColumnLinks columnLinks={column2Links} openFooterMenuLabel={openFooterMenuLabel} closeFooterMenuLabel={closeFooterMenuLabel} />
              <ColumnLinks columnLinks={column3Links} openFooterMenuLabel={openFooterMenuLabel} closeFooterMenuLabel={closeFooterMenuLabel} />
              <ColumnLinks columnLinks={column4Links} openFooterMenuLabel={openFooterMenuLabel} closeFooterMenuLabel={closeFooterMenuLabel} />
            </div>
            <div className="footer-navigation__social">
              {socialLinks && socialLinks.length > 0 ? (
                <ul className={"social-list-new"}>
                  {socialLinks.map((link, index) => {
                    const svg = link.socialIcon.split("@")[0]
                    return (
                      <li key={index} className={"social-list__item"}>
                        <a className="button button--social has-icon" title={link.linkText} href={link.linkUrl} target={link.linkTarget}>
                          {link.socialIconUpload ? (
                            <img alt="" tabIndex={-1} aria-hidden="true" className={"icon"} src={link.socialIconUpload} />
                          ) : (
                            <Svg isDecorative={true} iconName={svg} />
                          )}
                        </a>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                ""
              )}
              <div className="companyinfo">{ReactHtmlParser(contactInfoRichText)}</div>
              {redirectUrl ? (
                <a href={redirectUrl.originalString} className="redirect-link" target={redirectUrlTarget}>
                  <Svg iconName="arrowright-slim" isDecorative={true}></Svg>
                  <span className="text">{redirectUrlLabel}</span>
                </a>
              ) : undefined}
            </div>
          </div>
        </div>
      </div>
      <FooterBottom
        logoLabel={logoLabel}
        legalLinks={legalLinks}
        logoMobile={logoMobile}
        copyrightText={copyrightText}
        copyrightTextMobile={copyrightTextMobile}
      />
    </div>
  )
}

function FooterDesktop(props) {
  const {
    column1Links,
    column2Links,
    column3Links,
    column4Links,
    // appImage,
    // downloadAppText,
    // appStoreLink,
    // appStoreImage,
    // playStoreLink,
    // playStoreImage,
    socialLinks,
    legalLinks,
    logo,
    logoMobile,
    copyrightText,
    copyrightTextMobile,
    redirectUrl,
    redirectUrlLabel,
    redirectUrlTarget,
    contactInfoRichText,
    logoLabel,
  } = props

  // Render desktop structure
  return (
    <div className="footer-with-mobile-accordion">
      <div className="footer-container-new">
        <div className="w--wider">
          <div className="grid no-gutter">
            <div className="footer-navigation__social">
              {socialLinks && socialLinks.length > 0 ? (
                <ul className={"social-list-new"}>
                  {socialLinks.map((link, index) => {
                    const svg = link.socialIcon.split("@")[0]
                    return (
                      <li key={index} className={"social-list__item"}>
                        <a className="button button--social has-icon" title={link.linkText} href={link.linkUrl} target={link.linkTarget}>
                          {link.socialIconUpload ? (
                            <img className={"icon"} src={link.socialIconUpload} />
                          ) : (
                            <Svg isDecorative={true} iconName={svg} />
                          )}
                        </a>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                ""
              )}
              <div className="companyinfo">{ReactHtmlParser(contactInfoRichText)}</div>
              {redirectUrl ? (
                <a href={redirectUrl.originalString} className="redirect-link" target={redirectUrlTarget}>
                  <Svg iconName="arrowright-slim" isDecorative={true}></Svg>
                  <span className="text">{redirectUrlLabel}</span>
                </a>
              ) : undefined}
            </div>
            <div className="grid-item col--12 col-sm--5 col-md--8 footer-navigation__links-wrapper">
              <ColumnLinks columnLinks={column1Links} />
              <ColumnLinks columnLinks={column2Links} />
              <ColumnLinks columnLinks={column3Links} />
              <ColumnLinks columnLinks={column4Links} />
            </div>
          </div>
        </div>
      </div>
      <FooterBottom
        legalLinks={legalLinks}
        logo={logo}
        logoLabel={logoLabel}
        logoMobile={logoMobile}
        copyrightText={copyrightText}
        copyrightTextMobile={copyrightTextMobile}
      />
    </div>
  )
}

function Footer(props) {
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < 950

  return isMobile ? <FooterMobile {...props} /> : <FooterDesktop {...props} />
}

ColumnLinks.propTypes = {
  columnLinks: PropTypes.shape({
    columnLinks: PropTypes.array,
    headerLink: PropTypes.string,
    headerLinkText: PropTypes.string,
    headerLinkTarget: PropTypes.string,
  }),
}

LinkList.propTypes = {
  columnLinks: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      href: PropTypes.string,
      text: PropTypes.string,
    }),
  ),
}

Footer.propTypes = {
  column1Links: PropTypes.object,
  column2Links: PropTypes.object,
  column3Links: PropTypes.object,
  column4Links: PropTypes.object,
  appImage: PropTypes.string,
  downloadAppText: PropTypes.string,
  appStoreLink: PropTypes.string,
  appStoreImage: PropTypes.string,
  playStoreLink: PropTypes.string,
  playStoreImage: PropTypes.string,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      socialIcon: PropTypes.string,
      socialIconUpload: PropTypes.string,
      linkUrl: PropTypes.string,
      linkTarget: PropTypes.string,
    }),
  ),
  legalLinks: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string,
      href: PropTypes.string,
      text: PropTypes.string,
    }),
  ),
  logo: PropTypes.string,
  logoMobile: PropTypes.string,
  copyrightText: PropTypes.string,
  copyrightTextMobile: PropTypes.string,
}

export default Footer
